import { useCallback } from 'react';

import { type AirActionTrackingLocation, useTrackCanvasMoved } from '@air/analytics';
import { BilateralCornerArrows, Link, Move, Pencil, Remove, Trash } from '@air/next-icons';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';
import { useAirModal } from '@air/provider-modal';
import { useToasts } from '@air/provider-toast';
import { reportErrorToBugsnag } from '@air/utils-error';

import { DeleteCanvasConfirmationModal } from '~/components/Canvas/DeleteCanvasConfirmationModal';
import { EditCanvasDetailsModal } from '~/components/Canvas/EditCanvasDetailsModal';
import { MoveCanvasToBoardModal } from '~/components/Canvas/MoveCanvasToBoardModal/MoveCanvasToBoardModal';
import { SharePrivateCanvasModal } from '~/components/SharePrivateCanvasModal/SharePrivateCanvasModal';
import { useSelectedCanvasId } from '~/hooks/canvases/useSelectedCanvasId';
import { useBoardPermissionsCache } from '~/hooks/useBoardPermissionsCache';
import { useAccountContext } from '~/providers/AccountProvider';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { type CanvasListItem } from '~/swr-hooks/canvases/types';
import { usePrivateCanvasesUpdateCanvasMutation } from '~/swr-hooks/canvases/useUpdateCanvas';
import {
  canDeleteCanvasInBoard,
  canEditCanvasInBoard,
  canViewCanvasInBoard,
} from '~/utils/permissions/boardPermissions';
import { canViewShortUrl } from '~/utils/permissions/shortUrlPermissions';
import {
  canDeleteCanvasFromWorkspace,
  canEditCanvasInWorkspace,
  canManageCanvasesInWorkspace,
  canViewCanvasInWorkspace,
} from '~/utils/permissions/workspacePermissions';

export function useGetCanvasMenuOptionsCallback(boardId?: string) {
  const { selectCanvas } = useSelectedCanvasId();
  const [showEditCanvasDetailsModal] = useAirModal(EditCanvasDetailsModal);
  const [showDeleteCanvasConfirmationModal] = useAirModal(DeleteCanvasConfirmationModal);
  const [showMoveCanvasToBoardModal] = useAirModal(MoveCanvasToBoardModal);
  const [showSharePrivateCanvasModal] = useAirModal(SharePrivateCanvasModal);
  const { currentWorkspace } = useCurrentWorkspace();
  const { mutateAsync: updateCanvas } = usePrivateCanvasesUpdateCanvasMutation();
  const { trackCanvasMoved } = useTrackCanvasMoved();
  const { showToast } = useToasts();
  const { getBoardPermissions } = useBoardPermissionsCache();
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();
  const { data: account } = useAccountContext();

  const getCanvasMenuOptionsCallback = useCallback(
    ({
      item: canvas,
      trackLocation,
      boardId: boardIdOverride,
    }: {
      item: CanvasListItem;
      trackLocation: AirActionTrackingLocation;
      boardId?: string;
    }) => {
      const options: DropdownMenuOption[] = [];
      const effectiveBoardId = boardIdOverride ?? boardId;

      const boardPermissions = effectiveBoardId ? getBoardPermissions(effectiveBoardId) : undefined;
      const isOwner = !!account?.id && canvas.createdBy === account.id;
      const canManageCanvases = canManageCanvasesInWorkspace(workspacePermissions);

      // View rules: 194 (board, no ownership), 195/196 (workspace, canManageCanvases or own)
      const canView = effectiveBoardId
        ? canViewCanvasInBoard(boardPermissions)
        : canViewCanvasInWorkspace(workspacePermissions) && (canManageCanvases || isOwner);

      if (canView) {
        options.push({
          id: 'open',
          label: 'Open',
          type: 'item',
          prefix: <BilateralCornerArrows className="size-4" />,
          onSelect: () => {
            selectCanvas(canvas.id);
          },
        });
      }

      // Share rules: board — shortUrl.canView on board; workspace — own canvas or canManageCanvases + shortUrl.canView
      // Modal handles all sub-states (view-only, create, manage) internally
      const canShare = effectiveBoardId
        ? canViewShortUrl(boardPermissions)
        : canViewShortUrl(workspacePermissions) && (isOwner || canManageCanvases);

      if (canShare) {
        options.push({
          id: 'share',
          label: 'Share a link',
          type: 'item',
          prefix: <Link className="size-4" />,
          onSelect: () => {
            showSharePrivateCanvasModal({ canvasId: canvas.id, trackLocation });
          },
        });
      }

      // Edit rules (199, 200) are all own-only
      const canEdit =
        isOwner &&
        (effectiveBoardId ? canEditCanvasInBoard(boardPermissions) : canEditCanvasInWorkspace(workspacePermissions));

      if (canEdit) {
        if (options.length > 0) {
          options.push({ id: 'move-to-separator', type: 'separator' });
        }

        options.push({
          id: 'move-to',
          label: 'Move to...',
          prefix: <Move className="size-4" />,
          onSelect: () => {
            showMoveCanvasToBoardModal({
              canvasIds: [canvas.id],
              currentBoardId: canvas.boardId,
              trackLocation,
            });
          },
          type: 'item',
        });

        options.push({
          id: 'rename',
          label: 'Rename',
          type: 'item',
          prefix: <Pencil className="size-4" />,
          onSelect: () => {
            showEditCanvasDetailsModal({ canvasId: canvas.id });
          },
        });
      }

      // Remove from board is Canvas.update (edit action, own-only rule 199)
      const canRemoveFromBoard = effectiveBoardId ? canEditCanvasInBoard(boardPermissions) && isOwner : false;
      // Delete: rules 190/191 (canManageCanvases) OR 192/193 (own)
      const canDelete = effectiveBoardId
        ? canDeleteCanvasInBoard(boardPermissions) && (canManageCanvases || isOwner)
        : canDeleteCanvasFromWorkspace(workspacePermissions) && (canManageCanvases || isOwner);

      if (canRemoveFromBoard || canDelete) {
        if (options.length > 0) {
          options.push({ id: 'remove-separator', type: 'separator' });
        }

        if (canRemoveFromBoard) {
          options.push({
            id: 'remove-from-board',
            label: 'Remove from this board',
            prefix: <Remove className="size-4" />,
            onSelect: async () => {
              try {
                if (!currentWorkspace?.id) {
                  return;
                }
                await updateCanvas({
                  workspaceId: currentWorkspace.id,
                  canvases: [{ canvasId: canvas.id, boardId: null }],
                });
                trackCanvasMoved({
                  canvas_id: canvas.id,
                  original_location: boardId,
                  new_location: 'canvases',
                });
                showToast('Canvas removed from board');
              } catch (error) {
                showToast('There was an error updating the canvas.');
                reportErrorToBugsnag({
                  error,
                  context: 'Failed to update canvas',
                  metadata: { canvasId: canvas.id },
                });
              }
            },
            type: 'item',
          });
        }

        if (canDelete) {
          options.push({
            id: 'delete',
            label: 'Delete from workspace',
            type: 'item',
            className: 'text-red-9',
            prefix: <Trash className="size-4" />,
            onSelect: () => {
              showDeleteCanvasConfirmationModal({
                canvasIds: [canvas.id],
                boardId,
              });
            },
          });
        }
      }

      return options;
    },
    [
      account?.id,
      boardId,
      currentWorkspace?.id,
      selectCanvas,
      showEditCanvasDetailsModal,
      showDeleteCanvasConfirmationModal,
      showMoveCanvasToBoardModal,
      showSharePrivateCanvasModal,
      showToast,
      trackCanvasMoved,
      updateCanvas,
      getBoardPermissions,
      workspacePermissions,
    ],
  );

  return {
    getCanvasMenuOptionsCallback,
  };
}
